import { FC } from 'react';

import { Link } from '@remix-run/react';
import { Search } from '~/components/images/Search';
import { formatNumber } from '~/lib/utils';
import { getCategoryList, getHomepageCategoryList } from '~/services/api/categories';
import { AuctionStatus } from '~/types';

type CategoryListData = Awaited<ReturnType<typeof getCategoryList>>;
type HomepageCategoryListData = Awaited<ReturnType<typeof getHomepageCategoryList>>;
type CategoryListItem = CategoryListData['list'][0] | HomepageCategoryListData['list'][0];

interface ExploreCategoriesProps {
  data: CategoryListData | HomepageCategoryListData;
  title?: boolean;
  onCategoryClick?: (categoryName: string) => void;
}

const getCategoryProductCount = (category: CategoryListItem) => {
  if ('productCount' in category) return category.productCount;
  return category.productsToCategories.length;
};

const sumOfProducts = (categories: CategoryListItem[]) => {
  return categories.reduce((total, category) => {
    if ('approvedProductCount' in category) return total + category.approvedProductCount;

    return (
      total +
      category.productsToCategories.filter(
        (productToCategory) => productToCategory.product.auction?.status === AuctionStatus.APPROVED,
      ).length
    );
  }, 0);
};

const CategoryItem: FC<{
  category: CategoryListItem;
  onClick: (categoryName: string) => void;
}> = ({ category, onClick }) => {
  return (
    <Link onClick={() => onClick(category.name)} to={`/products?category=${category.name}`}>
      <div className="flex w-[150px] cursor-pointer flex-col items-center justify-center rounded-lg bg-grey1 p-4">
        <img src={category.image} alt={category.name} className="h-12 w-12 object-cover" loading="eager" />
        <p className="mt-3 text-sm font-medium text-black">{category.name}</p>
        <p className="text-xs font-normal text-slate-500">{formatNumber(getCategoryProductCount(category))} items</p>
      </div>
    </Link>
  );
};

const ExploreCategories: FC<ExploreCategoriesProps> = ({ data, title = true, onCategoryClick }) => {
  const categoriesWithoutOthers = data.list.filter((category) => category.name !== 'Others'); // Remove the last item (Others)
  const totalProducts = sumOfProducts(data.list); // Calculate the total number of products

  return (
    <div className="flex-col gap-4 py-5">
      <div className="border-gray100 border-b pb-4">
        {title && <p className="text-xl font-medium text-black">Explore Categories</p>}
      </div>
      <div className="flex items-center gap-4 overflow-x-scroll pb-4 pt-5 md:gap-10">
        {categoriesWithoutOthers.map((category) => (
          <CategoryItem key={category.name} category={category} onClick={onCategoryClick ?? (() => {})} />
        ))}
        <Link to={`/categories`}>
          <div className="flex w-[150px] cursor-pointer flex-col items-center justify-center rounded-lg bg-grey1 p-4">
            <div className="rounded-full bg-white p-2">{<Search className="h-8 w-8" />}</div>
            <p className="mt-3 text-sm font-medium text-black">Explore All</p>
            <p className="text-xs font-normal text-slate-500">{totalProducts} items</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ExploreCategories;
